import React, { useEffect, useRef } from 'react';

const HowItWorks = () => {
    const stepsRef = useRef(null);

    useEffect(() => {
        if (!stepsRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('show');
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        });

        const stepElements = stepsRef.current.querySelectorAll('.step-item');
        stepElements.forEach((element, index) => {
            element.style.transitionDelay = `${index * 150}ms`;
            observer.observe(element);
        });

        return () => {
            stepElements.forEach((element) => {
                observer.unobserve(element);
            });
        };
    }, []);

    const steps = [
        {
            title: "Crea tu página web",
            description: "Crea tu página web en menos de 10 minutos. Personalizala a tu gusto.",
            icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
            )
        },
        {
            title: "Muestra tus servicios",
            description: "Crea tu catálogo de servicios, combos, precios y promociones",
            icon: (
                <svg className="w-10 h-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
            )
        },
        {
            title: "Personaliza Tu Agenda",
            description: "Define horarios, servicios y disponibilidad de cada profesional en tu negocio.",
            icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
            )
        },
        {
            title: "Recibe Reservas Al Instante",
            description: "Tus clientes pueden reservar desde cualquier dispositivo. Tú recibes notificaciones en tiempo real.",
            icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
            )
        }
    ];

    return (
        <section className="py-8 relative overflow-hidden" id="como-funciona">
            {/* Decorative elements */}
            <div className="absolute -left-10 top-1/3 w-24 h-24 rounded-full bg-primary opacity-10 transform translate-y-[-50%]"></div>
            <div className="absolute right-0 bottom-1/3 w-32 h-32 rounded-full bg-secondary opacity-10 transform translate-y-[50%]"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-5xl font-bold mb-6 text-secondary">
                        Cómo Funciona Grovi
                    </h2>
                    <p className="text-xl text-gray-600">
                        Empezar a usar nuestra plataforma es rápido y sencillo para cualquier negocio de servicios
                    </p>
                </div>

                {/* Steps with animations */}
                <div className="relative" ref={stepsRef}>
                    {/* Connecting line */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-primary/20 transform -translate-x-1/2 md:block hidden"></div>

                    <div className="space-y-16 md:space-y-24">
                        {steps.map((step, index) => (
                            <div key={index} className={`step-item opacity-0 transform ${index % 2 === 0 ? 'translate-x-12' : '-translate-x-12'}`}>
                                <div className={`flex flex-col md:flex-row ${index % 2 === 0 ? '' : 'md:flex-row-reverse'} items-center`}>
                                    {/* Step number */}
                                    <div className="relative flex-shrink-0 z-10">
                                        <div className={`w-16 h-16 rounded-full border-4 border-primary flex items-center justify-center bg-white
                                            text-primary text-2xl font-bold shadow-xl transform transition-transform duration-300 hover:scale-110`}>
                                            {index + 1}
                                        </div>
                                        {/* Icon floating element */}
                                        <div className="absolute -top-2 -right-2 bg-secondary text-white p-2 rounded-lg shadow-lg transform rotate-12 hover:rotate-0 transition-transform duration-300">
                                            {step.icon}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className={`mt-6 md:mt-0 ${index % 2 === 0 ? 'md:ml-12' : 'md:mr-12'} max-w-lg w-full
                                        bg-white rounded-xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300
                                        transform hover:-translate-y-1 border-t-4 ${index % 2 === 0 ? 'border-primary' : 'border-secondary'}`}>
                                        <h3 className="text-2xl font-bold mb-4 text-gray-800">{step.title}</h3>
                                        <p className="text-lg text-gray-600">{step.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Final CTA */}
                <div className="text-center mt-20">
                    <div className="max-w-3xl mx-auto p-8 bg-secondary rounded-xl transform hover:scale-105 transition-all duration-300">
                        <h3 className="text-2xl font-bold text-white mb-4">¿Listo para empezar?</h3>
                        <p className="text-white/80 text-lg mb-6">
                            Transforma la gestión de tu negocio hoy mismo. Configuración en menos de 24 horas.
                        </p>
                        <a href="https://wa.me/59892748021?text=Hola!%20Me%20interesa%20conocer%20más%20sobre%20Grovi%20para%20mi%20negocio" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-white text-secondary font-semibold rounded-lg transition-transform duration-300 hover:scale-105 shadow-lg">
                            Solicitar una Demo
                            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>

            {/* Animation styles */}
            <style jsx="true">{`
                .step-item {
                    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
                }
                
                .step-item.show {
                    opacity: 1;
                    transform: translateX(0);
                }
            `}</style>
        </section>
    );
};

export default HowItWorks; 